/**
 * Universal entity lookup page.
 *
 * Accepts a prefixed ID in the URL, calls the API lookup endpoint,
 * and navigates to the resolved frontend page. Shows the org-switch
 * prompt when the entity belongs to a different organization.
 *
 * @module pages/Lookup
 * @see apps/api/src/lookup/routes.ts
 */

import React from 'react';
import { useParams, useNavigate } from '@tanstack/react-router';
import { API_URL } from '../../config';
import { Page, Spinner, Text } from '@chatsdet/ui';
import { OrgSwitchPrompt, type WrongOrgInfo } from '../components/OrgSwitchPrompt';
import { decodePrefixedId } from '@chatsdet/ids';

/** Map prefix to a human-readable resource type for the org switch prompt. */
const RESOURCE_TYPE_LABELS: Record<string, string> = {
  wf: 'flow',
  exec: 'test execution',
  trun: 'test run',
  wrun: 'run',
  iss: 'issue',
  prop: 'property',
  cred: 'credential',
};

export const Lookup: React.FC = () => {
  const { id } = useParams({ from: '/lookup/$id' });
  const navigate = useNavigate();
  const [error, setError] = React.useState<string | null>(null);
  const [wrongOrg, setWrongOrg] = React.useState<WrongOrgInfo | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    async function resolve() {
      try {
        const res = await fetch(`${API_URL}/lookup/${id}`, {
          credentials: 'include',
        });

        if (cancelled) return;

        const data = await res.json().catch(() => ({}));

        if (res.ok && data.path) {
          navigate({ to: data.path });
          return;
        }

        if (data.error?.includes('WRONG_ORG') && data.targetOrgId) {
          setWrongOrg({ orgId: data.targetOrgId, orgName: data.targetOrgName });
        } else {
          setError(data.message ?? data.error ?? 'Entity not found');
        }
      } catch {
        if (!cancelled) {
          setError('Failed to look up entity');
        }
      }
    }

    resolve();
    return () => { cancelled = true; };
  }, [id, navigate]);

  if (wrongOrg) {
    const decoded = decodePrefixedId(id);
    const resourceType = decoded ? (RESOURCE_TYPE_LABELS[decoded.prefix] ?? 'resource') : 'resource';

    return (
      <Page>
        <OrgSwitchPrompt
          resourceType={resourceType}
          targetOrgId={wrongOrg.orgId}
          targetOrgName={wrongOrg.orgName}
          redirectUrl={`/lookup/${id}`}
          onCancel={() => navigate({ to: '/' })}
        />
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-2">
          <Text size="lg">Lookup failed</Text>
          <Text color="muted">{error}</Text>
        </div>
      </Page>
    );
  }

  return (
    <Page>
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner size="lg" />
      </div>
    </Page>
  );
};
